import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import clsx from 'clsx';
import { getPayments } from '../api/payments';
import { Table } from '../components/ui/Table';
import Badge from '../components/ui/Badge';
import Pagination from '../components/ui/Pagination';
import SearchInput from '../components/ui/SearchInput';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { formatDate, formatCurrency } from '../lib/utils';

const PAGE_SIZE = 20;
const STATUS_FILTERS = ['all', 'pending', 'completed', 'failed', 'refunded'];

export default function Payments() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebouncedValue(search.trim());

  const { data, isLoading } = useQuery({
    queryKey: ['payments', page, statusFilter, debouncedSearch],
    queryFn: () =>
      getPayments({
        page,
        limit: PAGE_SIZE,
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      }).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const payments = Array.isArray(data) ? data : (data?.items ?? data?.payments ?? []);
  const total = Array.isArray(data) ? payments.length : (data?.total ?? payments.length);

  const statusColor = (s) => {
    if (s === 'completed') return 'success';
    if (s === 'pending') return 'warning';
    if (s === 'failed') return 'danger';
    if (s === 'refunded') return 'info';
    return 'default';
  };

  const columns = [
    { key: 'id', label: 'Payment ID', render: (v) => <span className="font-mono text-xs font-semibold text-gray-700 dark:text-gray-300">#{v}</span> },
    { key: 'customer_id', label: 'Customer', render: (v) => <span className="font-mono text-xs text-gray-500">#{v}</span> },
    { key: 'ride_id', label: 'Ride', render: (v) => <span className="font-mono text-xs text-gray-500">{v ? `#${v}` : '—'}</span> },
    { key: 'amount', label: 'Amount', render: (v) => <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(v)}</span> },
    {
      key: 'method', label: 'Method',
      render: (v) => <Badge variant="info">{v ?? '—'}</Badge>,
    },
    {
      key: 'status', label: 'Status',
      render: (v) => <Badge variant={statusColor(v)}>{v ?? '—'}</Badge>,
    },
    {
      key: 'created_at', label: 'Date',
      render: (v) => <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(v)}</span>,
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Payments</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{total} records</p>
        </div>
        <SearchInput
          placeholder="Search payment, ride, customer…"
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          className="w-full sm:w-80"
        />
      </div>

      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl w-fit flex-wrap">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors',
              statusFilter === s
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            )}
          >
            {s}
          </button>
        ))}
      </div>

      <Table
        columns={columns}
        data={payments}
        loading={isLoading}
        emptyMessage={debouncedSearch ? `No payments match “${debouncedSearch}”` : 'No payment records found'}
      />
      <Pagination page={page} limit={PAGE_SIZE} total={total} onPageChange={setPage} />
    </div>
  );
}
